import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AnalyticsService } from '@core/services/analytics.service';
import { EmailService } from '@core/services/email.service';
import { FormUtils } from '@core/utils/form-utils';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '@shared/services/language.service';
import { PublicGenericService } from '@shared/services/public-generic.service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import {
  CountryISO,
  NgxIntlTelInputModule,
  PhoneNumberFormat,
  SearchCountryField,
} from 'ngx-intl-tel-input-gg';
import { Subscription } from 'rxjs';
import {
  IUserEventRegistrationDto,
  IUserRegistrationDto,
  UserLogged,
} from '../../interfaces/login.interfaces';
import { LoginService } from '../../services/login.service';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [TranslateModule, FormsModule, RouterModule, NgxIntlTelInputModule],
})
@UntilDestroy()
export class SignUpComponent implements AfterViewInit, OnDestroy {
  @Input() nextPageRoute: string = 'account';
  @Input() idEvent: number | null = null;
  @Output() changePage = new EventEmitter<{
    to?: 'login' | 'register' | 'reset' | 'magicRequest';
    email?: string;
  }>();

  separateDialCode = false;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.Italy,
    CountryISO.UnitedStates,
    CountryISO.UnitedKingdom,
  ];

  private subscription: Subscription;

  userCredentials: IUserRegistrationDto | IUserEventRegistrationDto = {
    languageCode: 'it',
    username: '',
    password: '',
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    idCountry: 105,
    activeWithSMS: false,
    idEvent: this.idEvent,
    signUpPath: '/home',
    isAutonomousSeller: false,
  };
  userRegistration: UserLogged = new UserLogged(
    -1,
    '',
    '',
    '',
    -1,
    false,
    true,
    true,
    '',
    '',
    -1,
    ''
  );
  acceptPolicy = true;
  isProcessing = false;

  step = 0;
  emailExists = false;
  isFirstnameEmpty = false;
  isLastnameEmpty = false;
  isPhoneEmpty = false;
  policyNotAccepted = false;
  isUsernameEmpty = false;
  isUsernameInvalid = false;
  isPasswordEmpty = false;
  isEmailInvalid = false;

  // Switch to inject() pattern
  private loginService = inject(LoginService);
  private emailService = inject(EmailService);
  private languageService = inject(LanguageService);
  public publicGenericService = inject(PublicGenericService);
  public activeModal = inject(NgbActiveModal);
  private recaptchaV3Service = inject(ReCaptchaV3Service);
  private analyticsService = inject(AnalyticsService);
  private router = inject(Router);

  ngAfterViewInit() {
    this.emailService.showReCaptchaBadge();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.emailService.hideReCaptchaBadge();
  }

  // Replace legacy helper with a clearly named method used by template buttons
  triggerChangePage() {
    this.changePage.emit({});
  }

  validateUsername() {
    const username = this.userCredentials.username || '';
    this.isUsernameInvalid = username.includes('@');
  }

  changeStep(step: number) {
    this.userCredentials.email = this.userCredentials?.email
      ?.trim()
      ?.toLowerCase();
    this.userCredentials.phone =
      this.userCredentials.phoneData?.internationalNumber || '';

    if (step === 1) {
      if (this.userCredentials.firstName == '') {
        this.isFirstnameEmpty = true;
      } else {
        this.isFirstnameEmpty = false;
      }
      if (this.userCredentials.lastName == '') {
        this.isLastnameEmpty = true;
      } else {
        this.isLastnameEmpty = false;
      }
      if (FormUtils.emailRegex.test(this.userCredentials.email)) {
        this.isEmailInvalid = false;
      } else {
        this.isEmailInvalid = true;
      }
      if (this.userCredentials.phone == '') {
        this.isPhoneEmpty = true;
      } else {
        this.isPhoneEmpty = false;
      }
      if (!this.acceptPolicy) {
        this.policyNotAccepted = true;
      } else {
        this.policyNotAccepted = false;
      }
    }
    if (
      !this.isFirstnameEmpty &&
      !this.isLastnameEmpty &&
      !this.isEmailInvalid &&
      !this.isPhoneEmpty &&
      this.acceptPolicy
    ) {
      if (this.step === 0) {
        this.loginService
          .userExists(this.userCredentials.email)
          .subscribe((exists) => {
            if (exists) {
              this.emailExists = true;
              this.step = 99;
              this.loginService
                .requestMagicLogin({
                  email: this.userCredentials.email,
                  languageCode: this.languageService.userSelectedLanguage.code,
                  redirectPath: this.computeCurrentRedirectPath(),
                })
                .subscribe();
            } else {
              this.step = step;
            }
          });
      } else {
        this.step = step;
      }
    }
  }

  onDecisionLogin() {
    // switch dialog to login
    this.changePage.emit({ to: 'login' });
  }

  onDecisionResetPassword() {
    this.changePage.emit({ to: 'reset', email: this.userCredentials.email });
  }

  onDecisionMagicLink() {
    const email = this.userCredentials?.email?.trim()?.toLowerCase() || '';
    this.changePage.emit({ to: 'magicRequest', email });
  }

  onDecisionUseDifferentEmail() {
    this.emailExists = false;
    this.step = 0;
    this.userCredentials.email = '';
  }

  register(form: NgForm) {
    this.isProcessing = true;
    this.userCredentials.email = this.userCredentials?.email
      ?.trim()
      ?.toLowerCase();
    this.userCredentials.signUpPath = window.location.pathname;

    if (this.userCredentials.username == '') {
      this.isUsernameEmpty = true;
    } else {
      this.isUsernameEmpty = false;
    }
    this.validateUsername();
    if (this.userCredentials.password == '') {
      this.isPasswordEmpty = true;
    } else {
      this.isPasswordEmpty = false;
    }
    if (!this.isUsernameEmpty && !this.isUsernameInvalid && !this.isPasswordEmpty) {
      this.userCredentials.languageCode =
        this.languageService.userSelectedLanguage.code;

      if (form.invalid) {
        for (const control of Object.keys(form.controls)) {
          form.controls[control].markAsTouched();
        }
        return;
      }

      this.subscription = this.recaptchaV3Service
        .execute('registration')
        .subscribe((token: string) => {
          this.emailService
            .verifyReCaptcha({ responseToken: token })
            .pipe(untilDestroyed(this))
            .subscribe(() => {
              // fatto perché il parametro di input viene valorizzato in un secondo momento e la valorizzazione di userCredentials prende sempre null
              if (this.idEvent != this.userCredentials.idEvent) {
                this.userCredentials.idEvent = this.idEvent;
              }

              if (this.userCredentials.idEvent) {
                this.userCredentials.activeWithSMS = true;
                this.loginService
                  .registerForEvent(
                    this.userCredentials as IUserRegistrationDto
                  )
                  .subscribe((dataR) => {
                    this.userRegistration = dataR;
                    this.changeStep(2);
                    this.analyticsService.trackSignUp('registered');
                  });
              } else {
                this.userCredentials.activeWithSMS = false;
                this.loginService
                  .register(this.userCredentials as IUserRegistrationDto)
                  .subscribe((dataR) => {
                    this.userRegistration = dataR;
                    this.changeStep(2);
                    this.analyticsService.trackSignUp('registered');
                  });
              }

              this.isProcessing = false;
            });
        });
    } else {
      this.isProcessing = false;
    }
  }

  private computeCurrentRedirectPath(): string {
    const currentUrl = this.router.url || '';
    if (!currentUrl) return '';

    // Use Router to parse current URL into a UrlTree for robust extraction
    const tree = this.router.parseUrl(currentUrl);
    const primary = tree.root.children['primary'];
    const segments = primary?.segments ?? [];
    let path = segments.map((s) => s.path).join('/');

    // Strip language prefix (e.g., it/, en/)
    const lang = (
      this.languageService.userSelectedLanguage?.code || ''
    ).toLowerCase();
    if (lang && (path === lang || path.startsWith(lang + '/'))) {
      path = path.substring(lang.length + (path.length > lang.length ? 1 : 0));
    }

    // Rebuild query string
    const qp =
      tree.queryParams || ({} as Record<string, string | number | boolean>);
    const usp = new URLSearchParams();
    Object.keys(qp).forEach((k) => {
      const v = qp[k];
      if (v !== undefined && v !== null) {
        usp.set(k, String(v));
      }
    });
    const queryStr = usp.toString();
    const fragment = tree.fragment ? `#${tree.fragment}` : '';
    const rebuilt = queryStr
      ? `${path}?${queryStr}${fragment}`
      : `${path}${fragment}`;
    return rebuilt || 'home';
  }
}
